import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react/react/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PropsTable = makeShortcode("PropsTable");
const PropsTableRow = makeShortcode("PropsTableRow");
const InlineCode = makeShortcode("InlineCode");
const PropsTableSxRow = makeShortcode("PropsTableSxRow");
const ComponentChecklist = makeShortcode("ComponentChecklist");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {Overlay} from '@primer/react'
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`Overlay`}</inlineCode>{` component handles all behaviors needed by overlay UIs as well as the common styles that all overlays should have. `}<inlineCode parentName="p">{`Overlay`}</inlineCode>{` is the base component for many of our overlay-type components. Overlays use shadows to express elevation.`}</p>
    <p><strong parentName="p">{`Behaviors include:`}</strong></p>
    <ul>
      <li parentName="ul">{`Rendering the overlay in a React Portal so that it always renders on top of other content on the page`}</li>
      <li parentName="ul">{`Trapping focus`}</li>
      <li parentName="ul">{`Calling a user provided function when the user presses `}<inlineCode parentName="li">{`Escape`}</inlineCode></li>
      <li parentName="ul">{`Calling a user provided function when the user clicks outside of the container`}</li>
      <li parentName="ul">{`Focusing either user provided element, or the first focusable element in the container when it is opened`}</li>
      <li parentName="ul">{`Returning focus to an element when container is closed`}</li>
    </ul>
    <h2>{`Example`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "live noinline",
        "live": true,
        "noinline": true
      }}>{`const Example = () => {
  // you must manage your own open state
  const [isOpen, setIsOpen] = React.useState(false)
  const noButtonRef = React.useRef(null)
  const anchorRef = React.useRef(null)
  return (
    <>
      <Button ref={anchorRef} onClick={() => setIsOpen(!isOpen)}>
        open overlay
      </Button>
      {/* be sure to conditionally render the Overlay. This helps with performance and is required. */}
      {isOpen && (
        <Overlay
          initialFocusRef={noButtonRef}
          returnFocusRef={anchorRef}
          ignoreClickRefs={[anchorRef]}
          onEscape={() => setIsOpen(!isOpen)}
          onClickOutside={() => setIsOpen(false)}
          aria-labelledby="title"
        >
          <Box display="flex" flexDirection="column" p={2}>
            <Text id="title">Are you sure you would like to delete this item?</Text>
            <Button>yes</Button>
            <Button ref={noButtonRef}>no</Button>
          </Box>
        </Overlay>
      )}
    </>
  )
}

render(<Example />)
`}</code></pre>
    <h2>{`Accessibility considerations`}</h2>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` must either have:`}
        <ul parentName="li">
          <li parentName="ul">{`A value set for the `}<inlineCode parentName="li">{`aria-labelledby`}</inlineCode>{` attribute that refers to a visible title.`}</li>
          <li parentName="ul">{`An `}<inlineCode parentName="li">{`aria-label`}</inlineCode>{` attribute`}</li>
        </ul>
      </li>
      <li parentName="ul">{`If the `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` should also have a longer description, use `}<inlineCode parentName="li">{`aria-describedby`}</inlineCode></li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` component has a `}<inlineCode parentName="li">{`role="dialog"`}</inlineCode>{` set on it, if you are using `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` for alerts, you can pass in `}<inlineCode parentName="li">{`role="alertdialog"`}</inlineCode>{` instead. Please read the `}<a parentName="li" {...{
          "href": "https://www.w3.org/TR/wai-aria-1.1/#alertdialog"
        }}>{`W3C guidelines`}</a>{` to determine which role is best for your use case`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{` component has `}<inlineCode parentName="li">{`aria-modal`}</inlineCode>{` set to `}<inlineCode parentName="li">{`true`}</inlineCode>{` by default and should not be overridden as all `}<inlineCode parentName="li">{`Overlay`}</inlineCode>{`s behave as modals.`}</li>
    </ul>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices-1.1/#dialog_roles_states_props"
      }}>{`W3C accessibility recommendations for modals`}</a>{`.`}</p>
    <h2>{`Positioning`}</h2>
    <p><inlineCode parentName="p">{`Overlay`}</inlineCode>{` renders its `}<inlineCode parentName="p">{`children`}</inlineCode>{` within a div positioned absolutely within a portal within the default portal root. The overlay will not update its positioning if the portal root's positioning changes (e.g., if the portal root is statically positioned after some DOM element that dynamically resizes). You may consider using the `}<a parentName="p" {...{
        "href": "/AnchoredOverlay"
      }}>{`AnchoredOverlay`}</a>{` component or `}<a parentName="p" {...{
        "href": "/Portal#customizing-the-portal-root"
      }}>{`customizing the portal root`}</a>{` to achieve different positioning behavior.`}</p>
    <h2>{`Props`}</h2>
    <h3>{`Overlay`}</h3>
    <PropsTable mdxType="PropsTable">
  <PropsTableRow required name="returnFocusRef" type="React.RefObject<HTMLElement>" description={<>
        Ref for the element to focus when the <InlineCode mdxType="InlineCode">Overlay</InlineCode> is closed.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow required name="onClickOutside" type="function" description={<>
        Function to call when clicking outside of the <InlineCode mdxType="InlineCode">Overlay</InlineCode>. Typically this function sets the{' '}
        <InlineCode mdxType="InlineCode">Overlay</InlineCode> visibility state to <InlineCode mdxType="InlineCode">false</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow required name="onEscape" type="function" description={<>
        Function to call when user presses <InlineCode mdxType="InlineCode">Escape</InlineCode>. Typically this function sets the{' '}
        <InlineCode mdxType="InlineCode">Overlay</InlineCode> visibility state to <InlineCode mdxType="InlineCode">false</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="ignoreClickRefs" type="React.RefObject<HTMLElement> []" description={<>
        An array of ref objects to ignore clicks on in the onOutsideClick behavior. This is often used to ignore
        clicking on the element that toggles the open/closed state for the Overlay to prevent the Overlay from being
        toggled twice.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="initialFocusRef" type="React.RefObject<HTMLElement>" description={<>
        Ref for the element to focus when the <InlineCode mdxType="InlineCode">Overlay</InlineCode> is opened. If nothing is provided, the
        first focusable element in the <InlineCode mdxType="InlineCode">Overlay</InlineCode> body is focused.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="width" type={`| 'small' 
| 'medium' 
| 'large' 
| 'xlarge' 
| 'xxlarge' 
| 'auto'`} defaultValue="'auto'" description={<>
        Sets the width of the <InlineCode mdxType="InlineCode">Overlay</InlineCode>, pick from our set list of widths, or pass{' '}
        <InlineCode mdxType="InlineCode">auto</InlineCode> to automatically set the width based on the content of the{' '}
        <InlineCode mdxType="InlineCode">Overlay</InlineCode>. <InlineCode mdxType="InlineCode">small</InlineCode> corresponds to <InlineCode mdxType="InlineCode">256px</InlineCode>,{' '}
        <InlineCode mdxType="InlineCode">medium</InlineCode> corresponds to <InlineCode mdxType="InlineCode">320px</InlineCode>, <InlineCode mdxType="InlineCode">large</InlineCode>{' '}
        corresponds to <InlineCode mdxType="InlineCode">480px</InlineCode>, <InlineCode mdxType="InlineCode">xlarge</InlineCode> corresponds to{' '}
        <InlineCode mdxType="InlineCode">640px</InlineCode>, <InlineCode mdxType="InlineCode">xxlarge</InlineCode> corresponds to <InlineCode mdxType="InlineCode">960px</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="height" type={`| 'xsmall' 
| 'small' 
| 'medium' 
| 'large' 
| 'xlarge' 
| 'auto'`} defaultValue="'auto'" description={<>
        Sets the height of the <InlineCode mdxType="InlineCode">Overlay</InlineCode>, pick from our set list of heights, or pass{' '}
        <InlineCode mdxType="InlineCode">auto</InlineCode> to automatically set the height based on the content of the{' '}
        <InlineCode mdxType="InlineCode">Overlay</InlineCode>. <InlineCode mdxType="InlineCode">xsmall</InlineCode> corresponds to <InlineCode mdxType="InlineCode">192px</InlineCode>,{' '}
        <InlineCode mdxType="InlineCode">small</InlineCode> corresponds to <InlineCode mdxType="InlineCode">256px</InlineCode>, <InlineCode mdxType="InlineCode">medium</InlineCode>{' '}
        corresponds to <InlineCode mdxType="InlineCode">320px</InlineCode>, <InlineCode mdxType="InlineCode">large</InlineCode> corresponds to{' '}
        <InlineCode mdxType="InlineCode">432px</InlineCode>, <InlineCode mdxType="InlineCode">xlarge</InlineCode> corresponds to <InlineCode mdxType="InlineCode">600px</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="maxHeight" type={`| 'xsmall'
| 'small'
| 'medium'
| 'large'
| 'xlarge'`} description={<>
        Sets the maximum height of the <InlineCode mdxType="InlineCode">Overlay</InlineCode>, pick from our set list of heights.
        <InlineCode mdxType="InlineCode">xsmall</InlineCode> corresponds to <InlineCode mdxType="InlineCode">192px</InlineCode>, <InlineCode mdxType="InlineCode">small</InlineCode> corresponds
        to <InlineCode mdxType="InlineCode">256px</InlineCode>, <InlineCode mdxType="InlineCode">medium</InlineCode> corresponds to <InlineCode mdxType="InlineCode">320px</InlineCode>,{' '}
        <InlineCode mdxType="InlineCode">large</InlineCode> corresponds to <InlineCode mdxType="InlineCode">432px</InlineCode>, <InlineCode mdxType="InlineCode">xlarge</InlineCode> corresponds
        to <InlineCode mdxType="InlineCode">600px</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="visibility" type={`| 'visible' 
| 'hidden'`} defaultValue="'visible'" description={<>
        Sets the visibility of the <InlineCode mdxType="InlineCode">Overlay</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="anchorSide" type={`| 'inside-top' 
| 'inside-bottom' 
| 'inside-left' 
| 'inside-right' 
| 'inside-center' 
| 'outside-top' 
| 'outside-bottom' 
| 'outside-left' 
| 'outside-right'`} description={<>If provided, the Overlay will slide into position from the side of the anchor with a brief animation</>} mdxType="PropsTableRow" />
  <PropsTableRow name="top" type="number" defaultValue={0} description={<>
        Vertical position of the overlay, relative to its closest positioned ancestor (often its
        <InlineCode mdxType="InlineCode">Portal</InlineCode>).
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="left" type="number" defaultValue={0} description={<>
        Horizontal position of the overlay, relative to its closest positioned ancestor (often its{' '}
        <InlineCode mdxType="InlineCode">Portal</InlineCode>).
      </>} mdxType="PropsTableRow" />
  <PropsTableRow name="portalContainerName" type="string" description={<>
        If defined, Overlays will be rendered in the named portal. See also <InlineCode mdxType="InlineCode">Portal</InlineCode>.
      </>} mdxType="PropsTableRow" />
  <PropsTableSxRow mdxType="PropsTableSxRow" />
    </PropsTable>
    <h2>{`Status`}</h2>
    <ComponentChecklist items={{
      propsDocumented: true,
      noUnnecessaryDeps: true,
      adaptsToThemes: true,
      adaptsToScreenSizes: true,
      fullTestCoverage: false,
      usedInProduction: false,
      usageExamplesDocumented: false,
      designReviewed: false,
      a11yReviewed: false,
      stableApi: false,
      addressedApiFeedback: false,
      hasDesignGuidelines: false,
      hasFigmaComponent: false
    }} mdxType="ComponentChecklist" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      